// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek <roman.morawek@embyt.com>

import { isNum, isSet } from "shared/utils/utils";
import { Site } from "states/sitestates";

export interface IGeoBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

class SiteModel {
  // css class of the badge color
  public static getBadgeState(site: Site): string {
    if (!isSet(site.unackAlarms) || site.unackAlarms == 0) {
      return "ok";
    } else if (isSet(site.unclearedAlarms) && site.unclearedAlarms > 0) {
      return "alarm";
    }
    return "pending";
  }

  public static matchesFilterText(site: Site, filterText: string): boolean {
    const name = site.name?.toLowerCase();
    if (!name) {
      return false;
    }
    // space-separated words all need to occur (in any order), not the filter text as one phrase
    const words = filterText.toLowerCase().split(/\s+/).filter(Boolean);
    return words.every((word) => name.includes(word));
  }

  // (0, 0) and range limits like (90, 180) are placeholders for a location that was never set
  public static hasValidCoordinates(
    site: Site,
  ): site is Site & { latitude: number; longitude: number } {
    return (
      isNum(site.latitude) &&
      isNum(site.longitude) &&
      Math.abs(site.latitude) < 90 &&
      Math.abs(site.longitude) < 180 &&
      !(site.latitude === 0 && site.longitude === 0)
    );
  }

  public static isWithinBounds(site: Site, bounds: IGeoBounds | undefined): boolean {
    if (!bounds || !SiteModel.hasValidCoordinates(site)) {
      return false;
    }
    return (
      site.latitude >= bounds.south &&
      site.latitude <= bounds.north &&
      site.longitude >= bounds.west &&
      site.longitude <= bounds.east
    );
  }
}

export default SiteModel;
